import { useEffect, useState } from 'react';
import axios from 'axios';
import { StudentTeam } from './api/types';
import {
  StudyGroupQuery,
  useGetStudentTeamsQuery,
} from '../../../generated/graphqlModels';

export const useGetStudentTeams = (
  studyGroupPk: string,
  students: StudyGroupQuery['studyGroup']['students'],
): [
  StudentTeam[],
  {
    counter: number;
    isLoading: boolean;
    isSaving: boolean;
    isChanged: boolean;
    areAllStudentTeamsValid: boolean;
    isTeamAdded: boolean;
    setIsChanged: (isChanged: boolean) => void;
    deleteTeam: (index: number) => void;
    save: () => Promise<void>;
    setStudentTeams: (studentTeams: StudentTeam[]) => void;
  },
] => {
  const [counter, setCounter] = useState(0);
  const [studentTeams, setStudentTeams] = useState<StudentTeam[]>([]);
  const [initialStudentTeams, setInitialStudentTeams] = useState<StudentTeam[]>(
    [],
  );
  const [areAllStudentTeamsValid, setAreAllStudentTeamsValid] =
    useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isChanged, setIsChanged] = useState<boolean>(false);
  const { data: studentTeamsData, refetch } = useGetStudentTeamsQuery({
    variables: { studyGroupId: studyGroupPk },
    // skip: !!studyGroupPk,
  });

  useEffect(() => {
    setIsLoading(false);
    if (studentTeamsData && students) {
      const newStudentTeams: StudentTeam[] = studentTeamsData.studentTeams.map(
        (studentTeam) => ({
          pk: studentTeam.id,
          name: studentTeam.name,
          teamLeader: students.find((s) => s.pk === studentTeam.teamLeader.pk),
          members: studentTeam.members
            .map((m) => students.find((s) => s.pk === m.pk))
            .filter(
              (a) => a !== undefined,
            ) as StudyGroupQuery['studyGroup']['students'],
          studyGroup: studyGroupPk,
          isStudentTeamValid: true,
          isSaved: true,
          oldMembers: studentTeam.members
            .map((m) => students.find((s) => s.pk === m.pk))
            .filter(
              (a) => a !== undefined,
            ) as StudyGroupQuery['studyGroup']['students'],
          oldName: studentTeam.name,
        }),
      );
      setInitialStudentTeams([...newStudentTeams]);
      setStudentTeams([...newStudentTeams]);
    }
  }, [studentTeamsData, students]);

  useEffect(() => {
    if (studentTeams) {
      let hasChanges = false;
      let newAreAllStudentTeamsValid = true;
      const newStudentTeams = studentTeams.map((studentTeam) => {
        const tmpStudentTeamStatus = studentTeam.isStudentTeamValid;
        if (!studentTeam.teamLeader) {
          studentTeam.isStudentTeamValid = false;
        } else if (
          !studentTeam.members.find((m) => m.pk === studentTeam.teamLeader?.pk)
        ) {
          studentTeam.isStudentTeamValid = false;
        } else if (studentTeam.name.length === 0) {
          studentTeam.isStudentTeamValid = false;
        } else if (
          studentTeams.filter((s) => s.name === studentTeam.name).length > 1
        ) {
          studentTeam.isStudentTeamValid = false;
        } else {
          studentTeam.isStudentTeamValid = true;
        }

        if (!studentTeam.isStudentTeamValid) {
          newAreAllStudentTeamsValid = false;
        }
        if (tmpStudentTeamStatus !== studentTeam.isStudentTeamValid) {
          hasChanges = true;
        }
        return studentTeam;
      });
      if (hasChanges) {
        setStudentTeams([...newStudentTeams]);
      }
      if (areAllStudentTeamsValid !== newAreAllStudentTeamsValid) {
        setAreAllStudentTeamsValid(newAreAllStudentTeamsValid);
      }
    }
  }, [studentTeams]);

  const deleteTeam = (index: number) => {
    studentTeams.splice(index, 1);
    setStudentTeams([...studentTeams]);
    setIsChanged(true);
  };

  const save = async () => {
    setIsSaving(true);
    await Promise.all([
      ...studentTeams.map(async (studentTeam) => {
        if (studentTeam.isStudentTeamValid) {
          await axios.post(
            '/api/v1/clients/student_teams/',
            {
              name: studentTeam.name,
              study_group: studentTeam.studyGroup,
              team_leader: studentTeam.teamLeader!.pk,
              members: studentTeam.members.map((member) => member.pk),
              pk: studentTeam.pk,
            },
            {
              headers: {
                'X-CSRFToken': JSON.parse(
                  document.getElementById('csrf_token')!.innerHTML,
                ),
              },
            },
          );
          studentTeam.isSaved = true;
        }
      }),
      ...initialStudentTeams.map(async (oldStudentTeam) => {
        if (
          !studentTeams.find(
            (studentTeam) => studentTeam.pk === oldStudentTeam.pk,
          )
        ) {
          await axios.delete(
            `/api/v1/clients/student_teams/?pk=${oldStudentTeam.pk}`,
            {
              headers: {
                'X-CSRFToken': JSON.parse(
                  document.getElementById('csrf_token')!.innerHTML,
                ),
              },
            },
          );
        }
      }),
    ]);
    setIsSaving(false);
    setCounter(counter + 1);
    await refetch();
    setIsChanged(false);
  };
  const isTeamAdded = studentTeams.length > initialStudentTeams.length;

  return [
    studentTeams,
    {
      counter,
      isLoading,
      isSaving,
      isChanged,
      areAllStudentTeamsValid,
      isTeamAdded,
      deleteTeam,
      save,
      setIsChanged,
      setStudentTeams: (data: StudentTeam[]) => {
        setIsChanged(true);
        setStudentTeams(data);
      },
    },
  ];
};
